import { ENV } from '../config/env';
import { DateRangeChangeEvent, DateRangeValue as DateRangeFieldValue } from '../types/types';

export const DateRangePresetDays7 = '7jours';
export const DateRangePresetDays30 = '30jours';
export const DateRangePresetDays90 = '90jours';
export const DateRangePresetCustom = 'personnalisé';

export type DateRangePreset =
  | typeof DateRangePresetDays7
  | typeof DateRangePresetDays30
  | typeof DateRangePresetDays90
  | typeof DateRangePresetCustom;

export type DateRangeValue = {
  preset: DateRangePreset;
  start?: string | null;
  end?: string | null;
};

const getDatePresetDays = (preset: DateRangePreset) => {
  switch (preset) {
    case DateRangePresetDays7:
      return 7;
    case DateRangePresetDays30:
      return 30;
    case DateRangePresetDays90:
      return 90;
    default:
      return undefined;
  }
};

const computeDateRange = (nbDays: number) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - (nbDays - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
};

export const getDefaultCustomDateRange = () => {
  const defaultRangeDays = Number(ENV.DATE_RANGE_DEFAULT_LENGTH);
  const { start: startDate, end: endDate } = computeDateRange(defaultRangeDays);

  return {
    start: startDate,
    end: endDate,
  };
};

export const getSelectedDateRange = (dateRange: DateRangeValue) => {
  const preset = dateRange.preset;

  switch (preset) {
    case DateRangePresetCustom: {
      if (!dateRange.start || !dateRange.end) {
        // Si le preset n'est pas reconnu, on utilise le preset par défaut
        return getDefaultCustomDateRange();
      }

      const start = dateRange.start ? new Date(dateRange.start) : null;
      const end = dateRange.end ? new Date(dateRange.end) : null;

      // Set start time to beginning of day and end time to end of day
      if (start) {
        start.setHours(0, 0, 0, 0);
      }
      if (end) {
        end.setHours(23, 59, 59, 999);
      }

      return { start, end };
    }
    case DateRangePresetDays7:
    case DateRangePresetDays30:
    case DateRangePresetDays90:
    default: {
      const presetDays = getDatePresetDays(preset);
      const { start: startDate, end: endDate } = computeDateRange(presetDays ?? 0);

      return {
        start: startDate,
        end: endDate,
      };
    }
  }
};

export const isDateRangeValid = (
  startDate: Date | null,
  endDate: Date | null,
): { isRangeValid: boolean; errorMessage: string | null } => {
  if (!startDate || !endDate) {
    return { isRangeValid: false, errorMessage: null };
  }

  if (startDate > endDate) {
    return {
      isRangeValid: false,
      errorMessage: 'La date de fin doit être supérieure à la date de début',
    };
  }

  const maxRangeDays = Number(ENV.DATE_RANGE_MAX_LENGTH);
  const daysDifference = Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

  if (daysDifference > maxRangeDays) {
    return {
      isRangeValid: false,
      errorMessage: `La période sélectionnée ne doit pas dépasser ${maxRangeDays} jours`,
    };
  }

  return { isRangeValid: true, errorMessage: null };
};

export const formatDateValue = (dateString: string): string => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

export const validateDateRange = (startDate: Date | null, endDate: Date | null) => {
  const { isRangeValid, errorMessage } = isDateRangeValid(startDate, endDate);
  const dateDebutError = !startDate ? 'La date de début est obligatoire' : '';
  const dateFinError = !endDate ? 'La date de fin est obligatoire' : '';

  const isRangeInvalid = !isRangeValid;
  const rangeErrorMessage = isRangeInvalid ? errorMessage : null;

  return {
    isRangeInvalid,
    dateDebutError: dateDebutError || (rangeErrorMessage ?? ''),
    dateFinError,
    isValid: !isRangeInvalid && startDate && endDate,
  };
};

export interface RawDateRangeFieldValue {
  formattedValue: string;
  rawValue: string | null;
}

export interface RawDateRangeChangeEvent {
  target: {
    name?: string;
    id?: string;
    value: [RawDateRangeFieldValue, RawDateRangeFieldValue];
  };
}

export const adaptDateRangeEvent = (event: RawDateRangeChangeEvent): DateRangeChangeEvent => {
  const toAdapted = (v: RawDateRangeFieldValue): DateRangeFieldValue => ({
    formattedValue: v.formattedValue,
    rawValue: v.rawValue ? new Date(v.rawValue) : null,
  });
  const [start, end] = event.target.value;

  return {
    ...event,
    target: {
      ...event.target,
      value: [toAdapted(start), toAdapted(end)],
    },
  };
};

export const extractDatesFromEvent = (adaptedEvent: DateRangeChangeEvent) => {
  const [start, end] = adaptedEvent.target.value;
  return {
    startDate: start?.rawValue ? new Date(start.rawValue) : null,
    endDate: end?.rawValue ? new Date(end.rawValue) : null,
  };
};

export const createFormattedDateValues = (startDate: string, endDate: string): string[] => {
  return [formatDateForDisplay(startDate), formatDateForDisplay(endDate)];
};
